import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppUser } from '@/types/domain';
import { env } from '@/config/env';
import { repo } from '@/services';
import { DemoRepository } from '@/services/demo/demoRepository';
import { buildSeed } from '@/services/demo/seed';
import { getSupabase } from '@/lib/supabaseClient';
import { camelize } from '@/lib/case';
import { roleHas, type Permission } from './permissions';

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  isDemo: boolean;
  can: (permission: Permission) => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Demo mode only: impersonate a sample user to try different roles. */
  switchDemoUser: (userId: string) => void;
  /** Verifies the current password, then sets a new one. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Sets a new password for a session opened from a password-reset email link. */
  setRecoveredPassword: (newPassword: string) => Promise<void>;
  /** Emails a password-reset link that opens /reset-password. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Updates the signed-in user's own name/designation/phone. */
  updateMyProfile: (patch: { fullName: string; designation?: string | null; phone?: string | null }) => Promise<void>;
  /** True after the user arrives from a password-reset email link. */
  passwordRecovery: boolean;
  /** Set when the password was accepted but the emailed sign-in code is still needed. */
  loginCode: LoginCodeState | null;
  /** Emails a new 6-digit sign-in code for the current session. */
  requestLoginCode: () => Promise<{ sentTo: string; resendIn: number }>;
  /** Checks the code; on success the user is fully signed in. */
  verifyLoginCode: (code: string) => Promise<void>;
  /** Whether Brevo accepted the last code email ('pending' for a few seconds after sending). */
  loginCodeSendStatus: () => Promise<{ status: 'none' | 'pending' | 'sent' | 'failed'; detail?: string }>;
}

export interface LoginCodeState {
  emailMasked: string;
  /** A code was already sent for this session and is still valid. */
  codeActive: boolean;
  /** Seconds until another code may be requested. */
  resendIn: number;
}

interface LoginOtpStatusRow {
  required: boolean;
  verified: boolean;
  active: boolean;
  email_masked: string;
  code_active: boolean;
  resend_in: number;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const isDemo = !env.isSupabaseConfigured;
  const [user, setUser] = useState<AppUser | null>(isDemo ? buildSeed().users[0] : null);
  const [loading, setLoading] = useState(!isDemo);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [loginCode, setLoginCode] = useState<LoginCodeState | null>(null);

  const loadProfile = useCallback(async (userId: string | undefined) => {
    if (!userId) {
      setUser(null);
      setLoginCode(null);
      return;
    }
    // Two-step sign-in: until the emailed code is entered, the database shows this
    // session nothing, so ask for the code before loading anything else.
    const { data: otp, error: otpError } = await getSupabase().rpc('login_otp_status');
    if (!otpError && otp) {
      const st = otp as LoginOtpStatusRow;
      if (st.required && !st.verified) {
        setUser(null);
        setLoginCode({ emailMasked: st.email_masked, codeActive: st.code_active, resendIn: st.resend_in });
        return;
      }
    }
    setLoginCode(null);
    const { data } = await getSupabase()
      .from('profiles')
      .select('*, department:departments!profiles_department_id_fkey(name)')
      .eq('id', userId)
      .maybeSingle();
    if (!data || !data.is_active) {
      setUser(null);
      return;
    }
    setUser({ ...camelize<AppUser>(data), departmentName: (data.department as { name?: string } | null)?.name ?? null });
  }, []);

  useEffect(() => {
    if (isDemo) return;
    const sb = getSupabase();
    sb.auth.getSession().then(({ data }) => loadProfile(data.session?.user.id).finally(() => setLoading(false)));
    const { data: sub } = sb.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      // Defer DB calls out of the auth callback (avoids a known supabase-js lock-up).
      setTimeout(() => void loadProfile(session?.user.id), 0);
    });
    return () => sub.subscription.unsubscribe();
  }, [isDemo, loadProfile]);

  // If an administrator switches on the sign-in code while people are signed in, their
  // sessions are no longer allowed to read data: re-check now and then and show the code step.
  useEffect(() => {
    if (isDemo || !user) return;
    const check = async () => {
      const { data, error } = await getSupabase().rpc('login_otp_status');
      if (error || !data) return;
      const st = data as LoginOtpStatusRow;
      if (st.required && !st.verified) {
        setUser(null);
        setLoginCode({ emailMasked: st.email_masked, codeActive: st.code_active, resendIn: st.resend_in });
      }
    };
    const id = window.setInterval(() => void check(), 120_000);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [isDemo, user]);

  /** After the user sets their own password, stop asking them to change the temporary one. */
  const clearTemporaryPasswordFlag = useCallback(async () => {
    if (isDemo || !user?.mustChangePassword) return;
    await getSupabase().from('profiles').update({ must_change_password: false }).eq('id', user.id);
    setUser({ ...user, mustChangePassword: false });
  }, [isDemo, user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isDemo,
      can: (p) => roleHas(user?.role, p),
      signIn: async (email, password) => {
        if (isDemo) return;
        const { error } = await getSupabase().auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      signOut: async () => {
        if (isDemo) return;
        await getSupabase().auth.signOut();
        setUser(null);
        setLoginCode(null);
      },
      loginCode,
      requestLoginCode: async () => {
        const { data, error } = await getSupabase().rpc('request_login_otp');
        if (error) throw new Error(error.message);
        const r = (data ?? {}) as { sent_to?: string; resend_in?: number; already_verified?: boolean };
        if (r.already_verified) {
          const { data: s } = await getSupabase().auth.getSession();
          await loadProfile(s.session?.user.id);
        }
        return { sentTo: r.sent_to ?? '', resendIn: r.resend_in ?? 60 };
      },
      verifyLoginCode: async (code) => {
        const { data, error } = await getSupabase().rpc('verify_login_otp', { p_code: code });
        if (error) throw new Error(error.message);
        const r = (data ?? {}) as { verified?: boolean; error?: string };
        if (!r.verified) throw new Error(r.error ?? 'The code could not be verified.');
        const { data: s } = await getSupabase().auth.getSession();
        await loadProfile(s.session?.user.id);
      },
      loginCodeSendStatus: async () => {
        const { data, error } = await getSupabase().rpc('login_otp_send_status');
        if (error) return { status: 'none' };
        return data as { status: 'none' | 'pending' | 'sent' | 'failed'; detail?: string };
      },
      changePassword: async (currentPassword, newPassword) => {
        if (isDemo) throw new Error('Passwords cannot be changed in demo mode.');
        const sb = getSupabase();
        const email = user?.email;
        if (!email) throw new Error('You are not signed in.');
        // Checked in the database: signing in again would open a new session that needs a new email code.
        const { data: ok, error: verifyError } = await sb.rpc('check_my_password', { p_password: currentPassword });
        if (verifyError) throw verifyError;
        if (!ok) throw new Error('Current password is incorrect.');
        const { error } = await sb.auth.updateUser({ password: newPassword });
        if (error) throw error;
        await clearTemporaryPasswordFlag();
      },
      setRecoveredPassword: async (newPassword) => {
        if (isDemo) throw new Error('Passwords cannot be changed in demo mode.');
        const { error } = await getSupabase().auth.updateUser({ password: newPassword });
        if (error) throw error;
        setPasswordRecovery(false);
        await clearTemporaryPasswordFlag();
      },
      requestPasswordReset: async (email) => {
        if (isDemo) return;
        const { error } = await getSupabase().auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
      },
      updateMyProfile: async ({ fullName, designation, phone }) => {
        if (!user) throw new Error('You are not signed in.');
        const patch = { fullName, designation: designation ?? null, ...(phone !== undefined ? { phone } : {}) };
        await repo.users.updateContact(user.id, patch);
        // Re-read so the stored (normalised) phone number is shown.
        if (!isDemo) await loadProfile(user.id);
        else setUser((await repo.users.list()).find((u) => u.id === user.id) ?? user);
      },
      passwordRecovery,
      switchDemoUser: (userId) => {
        if (!isDemo) return;
        const next = buildSeed().users.find((u) => u.id === userId);
        if (next && repo instanceof DemoRepository) {
          repo.setCurrentUser(next.id);
          setUser(next);
        }
      },
    }),
    [user, loading, isDemo, passwordRecovery, loginCode, loadProfile, clearTemporaryPasswordFlag],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
